import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Calendar, Clock, Phone, Mail, User, MapPin } from 'lucide-react';
import axios from 'axios';
import { API_CONFIG } from '../../../config/api';

const statusConfig = {
  pending: { label: 'Beklemede', color: 'bg-yellow-500' },
  scheduled: { label: 'Planlandı', color: 'bg-blue-500' },
  completed: { label: 'Tamamlandı', color: 'bg-green-500' },
  cancelled: { label: 'İptal', color: 'bg-red-500' }
};

export default function AdminMeetingRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const res = await axios.get(`${API_CONFIG.BASE_URL}/backend/api.php?action=getMeetingRequests`);
      setRequests(res.data || []);
    } catch (error) {
      console.error('Error fetching meeting requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateStr, timeStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(`${dateStr}T${timeStr || '00:00:00'}`);
      return date.toLocaleString('tr-TR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return `${dateStr} ${timeStr || ''}`.trim();
    }
  };

  const getStatusBadge = (status) => {
    const cfg = statusConfig[status] || statusConfig.pending;
    return <Badge className={`${cfg.color} text-white`}>{cfg.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Toplantı talepleri yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 p-2 sm:p-4 lg:p-6 overflow-x-hidden max-w-full">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-0">
        <div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Toplantı Talepleri</h1>
          <p className="text-xs sm:text-sm text-gray-600 mt-1 sm:mt-2">
            Marka tasarım, dijital tasarım ve basılı materyal sayfalarından gelen toplantı taleplerini görüntüleyin.
          </p>
        </div>
        <div className="flex items-center justify-between sm:justify-end gap-4 sm:gap-4">
          <div className="text-center sm:text-right">
            <p className="text-xl sm:text-2xl font-bold text-purple-600">{requests.length}</p>
            <p className="text-xs sm:text-sm text-gray-600">Toplam Talep</p>
          </div>
          <div className="text-center sm:text-right">
            <p className="text-xl sm:text-2xl font-bold text-yellow-600">
              {requests.filter((r) => r.status === 'pending').length}
            </p>
            <p className="text-xs sm:text-sm text-gray-600">Bekleyen</p>
          </div>
        </div>
      </div>

      {requests.length === 0 ? (
        <div className="flex items-center justify-center h-40">
          <p className="text-gray-500 text-sm">Henüz toplantı talebi bulunmuyor.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
          {requests.map((req) => (
            <Card key={req.id} className="border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between gap-2">
                  <CardTitle className="text-base sm:text-lg flex items-center gap-2">
                    <User className="w-4 h-4 text-purple-600" />
                    <span className="truncate">{req.customer_name}</span>
                  </CardTitle>
                  {getStatusBadge(req.status)}
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                  <Calendar className="w-3 h-3" />
                  <span>{formatDateTime(req.meeting_date, req.meeting_time)}</span>
                  {req.source && (
                    <>
                      <span className="mx-1">•</span>
                      <MapPin className="w-3 h-3" />
                      <span>{req.source}</span>
                    </>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-3 text-xs sm:text-sm">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-gray-500" />
                    <a href={`mailto:${req.customer_email}`} className="text-blue-600 hover:underline break-all">
                      {req.customer_email}
                    </a>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-gray-500" />
                    <a href={`tel:${req.customer_phone}`} className="text-gray-800 hover:underline break-all">
                      {req.customer_phone}
                    </a>
                  </div>
                </div>
                {req.notes && (
                  <div>
                    <p className="font-semibold text-gray-800 mb-1">Not</p>
                    <p className="text-gray-700 bg-gray-50 p-2 rounded-lg whitespace-pre-line">
                      {req.notes}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

